// src/pages/schools/AcademicSessionPage.js
import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Divider,
  TextField,
  Chip,
  Stack,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import Swal from "sweetalert2";
import api from "../../api";
import { motion } from "framer-motion";
import { AddCircleOutline, Edit, Refresh } from "@mui/icons-material";

/**
 * Button style helper (rounded pill)
 */
function statusButtonStyle(current, selected) {
  const base = {
    padding: "6px 14px",
    borderRadius: 999,
    fontSize: 13,
    cursor: current === selected ? "default" : "pointer",
    fontWeight: 700,
    minWidth: 92,
    textAlign: "center",
    border: current === selected ? "none" : "1px solid rgba(0,0,0,0.08)",
    color: "#fff",
    transition: "all 140ms ease",
    boxShadow: current === selected ? "0 6px 18px rgba(0,0,0,0.08)" : "none",
  };
  if (current === "active") {
    return { ...base, background: "#2e7d32", opacity: current === selected ? 1 : 0.16 };
  }
  if (current === "upcoming") {
    return { ...base, background: "#1565c0", opacity: current === selected ? 1 : 0.16 };
  }
  // expired
  return { ...base, background: "#9e9e9e", opacity: current === selected ? 1 : 0.16 };
}

const AcademicSessionPage = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  

  const [form, setForm] = useState({
    id: "",
    session_name: "",
    start_date: "",
    end_date: "",
  });

  const today = new Date().toISOString().slice(0, 10); // 'YYYY-MM-DD'

  // Fetch sessions
  const fetchSessions = async () => {
    try {
      const res = await api.get("school/academic_year/list");
      let data = res.data?.data || [];

      // normalize status
      data = data.map((d) => ({
        ...d,
        status: String(d.status || "").toLowerCase(),
      }));

      // 🔥 ORDER BY id DESC (latest first)
      data.sort((a, b) => Number(b.id) - Number(a.id));

      setSessions(data);
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to fetch sessions.", "error");
    }
  };

  useEffect(() => {
    fetchSessions();
  }, []);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const resetForm = () => {
    setForm({ id: "", session_name: "", start_date: "", end_date: "" });
    setEditMode(false);
  };

  // Add / Edit
  const handleSubmit = async () => {
    if (!form.start_date || !form.end_date) {
      return Swal.fire("Missing", "Fill all required fields.", "warning");
    }
    // start <= end check
    if (new Date(form.start_date) > new Date(form.end_date)) {
      return Swal.fire("Invalid dates", "Start date must be before or equal to End date.", "warning");
    }

    setLoading(true);
    try {
      const url = editMode ? `school/academic_year/edit/${form.id}` : "school/academic_year/add";
      const res = await api.post(url, form);
      Swal.fire("Success", res?.data?.message || (editMode ? "Session Updated!" : "Session Added!"), "success");
      resetForm();
      fetchSessions();
    } catch (error) {
      console.error(error);
      const msg = error?.response?.data?.message || JSON.stringify(error?.response?.data) || "Operation failed.";
      Swal.fire("Error", msg, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    setForm({
      id: item.id,
      session_name: item.session_name,
      start_date: item.start_date,
      end_date: item.end_date,
    });
    setEditMode(true);
  };

  const getChipColor = (status) => (status === "active" ? "success" : status === "upcoming" ? "primary" : "default");

  /**
   * FRONTEND GUARDS BEFORE SENDING request:
   * - If target session is expired (end_date < today): disallow making it active/upcoming.
   * - Before making UPCOMING: ensure there's no other upcoming (client-side check).
   * - Before making ACTIVE: allowed (server will expire previous active). But disallow if target is expired.
   *
   * Server still authoritative — these are helpful UX guards to avoid unnecessary calls.
   */
  const updateStatus = async (id, status) => {
    const target = sessions.find((s) => String(s.id) === String(id));
    if (!target) {
      return Swal.fire("Error", "Session not found", "error");
    }

    // ❌ expired session cannot be UPCOMING
    if (status === "upcoming" && new Date(target.end_date) < new Date(today)) {
      return Swal.fire(
        "Invalid",
        "Expired session cannot be set as upcoming",
        "warning"
      );
    }

    // ✅ ACTIVE → always allowed (expired / upcoming / active)
    // ❌ NO CHECK HERE

    // Confirm
    const { isConfirmed } = await Swal.fire({
      title: "Confirm status change",
      text: `Set session "${target.session_name}" as ${status.toUpperCase()}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, change",
    });

    if (!isConfirmed) return;

    try {
      const res = await api.post(
        "school/academic_year/change_status",
        { id, status }
      );

      Swal.fire(
        "Success",
        res?.data?.message || "Status updated!",
        "success"
      );

      await fetchSessions();
    } catch (err) {
      console.error("updateStatus error:", err);
      const message =
        err?.response?.data?.message ||
        JSON.stringify(err?.response?.data) ||
        "Failed to update status";

      Swal.fire("Error", message, "error");
    }
  };


  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight="bold" textAlign="center">
        🎓 Academic Session Management
      </Typography>

      <Box display="flex" gap={3} mt={3} flexWrap="wrap">
        {/* Left */}
        <Paper sx={{ p: 3, width: 360, flexShrink: 0, borderRadius: 3 }} component={motion.div}>
          <Typography variant="h6" fontWeight={700}>
            {editMode ? "✏ Edit Session" : "➕ Add New Session"}
          </Typography>
          <Divider sx={{ my: 2 }} />

          <Stack spacing={2}>
            <TextField label="Session Name" name="session_name" value={form.session_name} onChange={handleChange} placeholder="e.g. 2025-2026" fullWidth />
            <TextField label="Start Date" type="date" name="start_date" value={form.start_date} onChange={handleChange} InputLabelProps={{ shrink: true }} fullWidth />
            <TextField label="End Date" type="date" name="end_date" value={form.end_date} onChange={handleChange} InputLabelProps={{ shrink: true }} fullWidth />

            <Stack direction="row" spacing={2}>
              <Button variant="contained" color={editMode ? "warning" : "primary"} onClick={handleSubmit} startIcon={<AddCircleOutline />} disabled={loading} fullWidth>
                {editMode ? "Update Session" : "Add Session"}
              </Button>

              {editMode && (
                <Button variant="outlined" color="inherit" onClick={resetForm}>
                  Cancel
                </Button>
              )}
            </Stack>
          </Stack>
        </Paper>

        {/* Right */}
        <Paper sx={{ p: 3, flexGrow: 1, borderRadius: 3 }} component={motion.div}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" fontWeight={700}>
              📋 Session List
            </Typography>

            <IconButton onClick={fetchSessions} size="large">
              <Refresh />
            </IconButton>
          </Box>

          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Session</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Start</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>End</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>
                  Change
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>
                  Edit
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sessions.map((s) => {
                const isExpiredByDate = new Date(s.end_date) < new Date(today);
                return (
                  <TableRow key={s.id} sx={{ "&:hover": { background: "#fbfdff" } }}>
                    <TableCell>{s.session_name || `${s.start_date?.slice(0, 4)}-${s.end_date?.slice(0, 4)}`}</TableCell>
                    <TableCell>{s.start_date}</TableCell>
                    <TableCell>{s.end_date}</TableCell>

                    <TableCell>
                      <Chip label={String(s.status).toUpperCase()} color={getChipColor(s.status)} size="small" />
                      {isExpiredByDate && <Typography component="span" sx={{ ml: 1, color: "text.secondary", fontSize: 12 }}> (by date)</Typography>}
                    </TableCell>

                    <TableCell align="center">
                      <Box sx={{ display: "flex", gap: 1, justifyContent: "center", alignItems: "center" }}>

                        {/* ACTIVE */}
                        <Tooltip
                          title={
                            s.status === "active"
                              ? "Already active"
                              : "Set as active (this will expire all other sessions)"
                          }
                        >
                          <div
                            role="button"
                            aria-disabled={s.status === "active"}
                            style={{ opacity: s.status === "active" ? 0.45 : 1 }}
                            onClick={() =>
                              s.status !== "active"
                                ? updateStatus(s.id, "active")
                                : null
                            }
                          >
                            <div style={statusButtonStyle("active", s.status)}>
                              ACTIVE
                            </div>
                          </div>
                        </Tooltip>

                        {/* UPCOMING */}
                        <Tooltip
                          title={
                            s.status === "upcoming"
                              ? "Already upcoming"
                              : isExpiredByDate
                              ? "Expired sessions cannot be upcoming"
                              : "Set as upcoming (only one upcoming allowed)"
                          }
                        >
                          <div
                            role="button"
                            aria-disabled={s.status === "upcoming" || isExpiredByDate}
                            style={{
                              opacity:
                                s.status === "upcoming" || isExpiredByDate ? 0.45 : 1,
                            }}
                            onClick={() =>
                              s.status !== "upcoming" && !isExpiredByDate
                                ? updateStatus(s.id, "upcoming")
                                : null
                            }
                          >
                            <div style={statusButtonStyle("upcoming", s.status)}>
                              UPCOMING
                            </div>
                          </div>
                        </Tooltip>

                        {/* EXPIRED */}
                        <Tooltip title={s.status === "expired" ? "Already expired" : "Mark as expired"}>
                          <div
                            role="button"
                            aria-disabled={s.status === "expired"}
                            style={{ opacity: s.status === "expired" ? 0.45 : 1 }}
                            onClick={() =>
                              s.status !== "expired"
                                ? updateStatus(s.id, "expired")
                                : null
                            }
                          >
                            <div style={statusButtonStyle("expired", s.status)}>
                              EXPIRED
                            </div>
                          </div>
                        </Tooltip>

                      </Box>
                    </TableCell>


                    <TableCell align="center">
                      <Tooltip title="Edit">
                        <IconButton onClick={() => handleEdit(s)} size="small">
                          <Edit color="primary" fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      </Box>
    </Box>
  );
};

export default AcademicSessionPage;
